import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { IonList, IonButton, IonTextarea, IonSpinner } from "@ionic/react";

import { IRealizationConversation } from "../../state";
import conversationIcon from "../../assets/icons/conversation.svg";

import styles from "./ConversationList.module.scss";

export interface ConversationListProps {
  conversations: IRealizationConversation[];
  calling?: boolean;
  createConversationReply?: (conversation_id: number, content: string) => void;
  createRealizationConversation?: (realization_id: number) => void;
  error: string;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  createConversationReply,
  createRealizationConversation,
  calling,
  error,
}) => {
  const [replyContent, setReplyContent] = useState<Record<number, string>>({});
  const previousConversationsRef = useRef<IRealizationConversation[]>([]);
  const [displayedContent, setDisplayedContent] = useState<
    Record<number, string>
  >({});
  const [typewriterActive, setTypewriterActive] = useState<
    Record<number, boolean>
  >({});
  const typewriterRefs = useRef<Record<number, number>>({});
  const textareaRefs = useRef<Record<number, HTMLIonTextareaElement | null>>(
    {},
  );

  const handleReply = (conversation_id: number) => {
    const content = replyContent[conversation_id];
    if (!content?.trim()) return;

    if (createConversationReply) {
      createConversationReply(conversation_id, content.trim());
      setReplyContent(prev => ({ ...prev, [conversation_id]: "" }));
    }
  };

  const updateReplyContent = (conversation_id: number, content: string) => {
    setReplyContent(prev => ({ ...prev, [conversation_id]: content }));
  };

  const handleRetry = (conversation_id: number) => {
    const conversation = conversations.find(
      conv => conv.id === conversation_id,
    );
    if (createRealizationConversation && conversation) {
      createRealizationConversation(conversation.realization_id);
    }
  };

  const handleTextareaFocus = (conversation_id: number) => {
    setTimeout(() => {
      const textarea = textareaRefs.current[conversation_id];
      if (textarea) {
        textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const shouldShowReplyArea = (conversation: IRealizationConversation) => {
    return (
      createConversationReply &&
      conversation.can_reply &&
      !conversation.conversation_ended &&
      conversation.status !== "failed" &&
      (!conversation.replies || conversation.replies.length === 0)
    );
  };

  const startTypewriter = useCallback(
    (conversationId: number, targetText: string) => {
      if (typewriterRefs.current[conversationId]) {
        clearTimeout(typewriterRefs.current[conversationId]);
      }

      setTypewriterActive(prev => ({ ...prev, [conversationId]: true }));
      setDisplayedContent(prev => ({ ...prev, [conversationId]: "" }));

      let currentIndex = 0;
      const typeSpeed = 30;

      const typeNextChar = () => {
        if (currentIndex < targetText.length) {
          setDisplayedContent(prev => ({
            ...prev,
            [conversationId]: targetText.slice(0, currentIndex + 1),
          }));
          currentIndex++;
          typewriterRefs.current[conversationId] = window.setTimeout(
            typeNextChar,
            typeSpeed,
          );
        } else {
          setTypewriterActive(prev => ({ ...prev, [conversationId]: false }));
          delete typewriterRefs.current[conversationId];
        }
      };

      typeNextChar();
    },
    [],
  );

  useEffect(() => {
    const previousConversations = previousConversationsRef.current;

    conversations.forEach(conversation => {
      const previousConversation = previousConversations.find(
        prev => prev.id === conversation.id,
      );

      if (!previousConversation) {
        setDisplayedContent(prev => ({
          ...prev,
          [conversation.id]: conversation.content,
        }));
        return;
      }

      const wasGenerating = previousConversation.status === "generating";
      const isNowCompleted = conversation.status === "completed";
      const contentChanged =
        previousConversation.content !== conversation.content;

      if (wasGenerating && isNowCompleted && contentChanged) {
        startTypewriter(conversation.id, conversation.content);
      } else if (!wasGenerating && !typewriterActive[conversation.id]) {
        setDisplayedContent(prev => ({
          ...prev,
          [conversation.id]: conversation.content,
        }));
      }
    });

    previousConversationsRef.current = conversations.map(conv => ({ ...conv }));
  }, [conversations, startTypewriter, typewriterActive]);

  useEffect(() => {
    const currentRefs = typewriterRefs.current;
    return () => {
      Object.values(currentRefs).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const getMessageDisplay = useMemo(() => {
    return (conversation: IRealizationConversation) => {
      if (conversation.status === "generating") {
        return (
          <span className={styles.generatingText}>
            <IonSpinner name="dots" className={styles.spinner} />
            会話を生成中
          </span>
        );
      }

      if (conversation.status === "failed") {
        return (
          <span className={styles.failedText}>
            会話の生成に失敗しました。
            <br />
            少し時間をおいてからもう一度お試しください。
          </span>
        );
      }

      return (
        <>
          {displayedContent[conversation.id] || conversation.content}
          {typewriterActive[conversation.id] && (
            <span className={styles.cursor}>|</span>
          )}
        </>
      );
    };
  }, [displayedContent, typewriterActive]);

  if (!conversations || conversations.length === 0) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.conversationWrapper}>
        <div>
          <img
            src={conversationIcon}
            alt="会話アイコン"
            className={styles.conversationIcon}
          />
        </div>
        <div className={styles.conversationCard}>
          {conversations.length > 0 && (
            <div className={styles.headerWrapper}>
              <div className={styles.headerContent}>
                <span className={styles.headerTitle}>GEAR</span>
                <span className={styles.headerSubtitle}>会話してみよう！</span>
              </div>
            </div>
          )}
          <IonList className={styles.conversationList}>
            {conversations.map((conversation, idx) => (
              <div
                key={`${conversation.id}-${idx}`}
                className={styles.conversationItem}
              >
                <div>
                  <div className={styles.messageContent}>
                    <p className={styles.messageText}>
                      {getMessageDisplay(conversation)}
                    </p>
                  </div>
                </div>

                {conversation.status === "failed" && (
                  <div className={styles.retryArea}>
                    <IonButton
                      fill="outline"
                      size="small"
                      disabled={calling}
                      onClick={() => handleRetry(conversation.id)}
                      className={styles.retryButton}
                    >
                      もう一度試す
                    </IonButton>
                    {error && <div className={styles.errorText}>{error}</div>}
                  </div>
                )}

                {conversation.replies?.map((reply, rIdx) => (
                  <div
                    key={`${reply.id}-${rIdx}`}
                    className={styles.userMessageWrapper}
                  >
                    <div className={styles.userMessage}>{reply.content}</div>
                  </div>
                ))}
                {shouldShowReplyArea(conversation) && (
                  <div className={styles.inputArea}>
                    <IonTextarea
                      ref={el => {
                        textareaRefs.current[conversation.id] = el;
                      }}
                      placeholder="GEARと会話する"
                      value={replyContent[conversation.id] || ""}
                      onIonChange={e => {
                        const value = e.detail.value;
                        if (value !== null && value !== undefined) {
                          updateReplyContent(conversation.id, value);
                        }
                      }}
                      onFocus={() => handleTextareaFocus(conversation.id)}
                      className={styles.textInput}
                      autoGrow={true}
                      rows={1}
                      debounce={0}
                    />
                    <IonButton
                      fill="clear"
                      size="small"
                      disabled={
                        calling || !replyContent[conversation.id]?.trim()
                      }
                      onClick={() => handleReply(conversation.id)}
                      className={styles.sendButton}
                    >
                      送信
                    </IonButton>
                  </div>
                )}

                {conversation.conversation_ended && (
                  <div className={styles.endedMessage}>
                    会話が終了しました。
                    <br />
                    活かし先を使うとまたGEARからコメントが届きます。
                  </div>
                )}
              </div>
            ))}
          </IonList>
        </div>
      </div>
    </div>
  );
};

export default ConversationList;
